// Form is based on Formik
// Data validation is based on Yup
// Please, be familiar with article first:
// https://hackernoon.com/react-form-validation-with-formik-and-yup-8b76bda62e10
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Theme,
} from '@mui/material';
import { green } from '@mui/material/colors';
import makeStyles from '@mui/styles/makeStyles';
import { FieldArray, FormikProps } from 'formik';
import React, { ChangeEvent, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useRecoilValueLoadable, useSetRecoilState } from 'recoil';
import { MaterialAutocomplete } from '../../../../../common/formik-material-ui';
import '../../../../../common/formik-material-ui/commaSeparatedEmails';
import {
  methodNameEnum,
  refetchToggle,
} from '../../../../../recoil/selectors/makePHPRequest';
import {
  endpointEnum,
  makeAPIRequest,
} from '../../../../../services/rest/makeAPIRequest';
import { DefaultActionSkeleton } from '../../../../components/dropdowns/DefaultAction';
import { useSnackbarWithStyles } from '../../../../components/notifications/SnackbarProviderWithStyles';
import { ViewQueueTeam } from '../../../../pages/EditQueueTeam';
import { IQueueEditFormInitialValues } from './QueueEditForm';

const useStyles = makeStyles((theme: Theme) => ({
  field: {
    marginTop: theme.spacing(1), // space between fields
    marginBottom: theme.spacing(1), // space between fields
  },
  table: {
    marginTop: theme.spacing(2), // align with fields
    marginBottom: theme.spacing(2), // align with fields
  },
  deleteIconButton: {
    color: theme.custom.button.delete.main,
  },
  buttonProgress: {
    color: green[500],
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -12,
    marginLeft: -12,
  },
  fabProgress: {
    color: green[500],
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -22,
    marginLeft: -22,
  },
}));

// create bitmap
// eslint-disable-next-line no-unused-vars, autofix/no-unused-vars
enum inProgressEnum {
  ADD_TEAM_QUEUE = 1, // 2^0
  DELETE_QUEUE_TEAM = 2, // 2^1
}

export interface IQueueTeamsContent {
  teamid: string;
  teamname: string;
  memberCount: string;
  lastUpdated?: string;
  queueteamid?: string;
}

interface IQueueTeams {
  id?: string;
  formik: FormikProps<IQueueEditFormInitialValues>;
  onTeamChange: () => void;
}

/**
 * Queue teams table and member add
 *
 * @param {Object} props
 * @param {Object} props.id Queue ID
 *
 * @see  https://stackoverflow.com/questions/48901016/how-to-set-change-field-value-from-external-user-action-react-final-form final-form mutators
 */
export const QueueTeams = ({ id, formik, onTeamChange }: IQueueTeams) => {
  const classes = useStyles();
  const [autocomplete, setAutocomplete] = useState<any>([]);
  const businessTeams = useRecoilValueLoadable(
    makeAPIRequest({
      endpoint: endpointEnum.GET_BUSINESS_TEAMS,
      payloadJsonString: JSON.stringify({
        call: methodNameEnum.GET_BUSINESS_TEAMS,
      }),
    })
  );
  // get businessTeams that do not belong to queue
  const teamsNotInQueue = useRef<any>([]);
  teamsNotInQueue.current = useMemo(
    () =>
      businessTeams.state === 'loading'
        ? teamsNotInQueue.current
        : businessTeams.contents?.response?.data?.result
            ?.filter(
              (businessTeam: any) =>
                !formik.values.queueTeams?.find(
                  (team: IQueueTeamsContent) =>
                    team.teamid === businessTeam.teamid
                )
            )
            ?.sort((a: any, b: any) =>
              a?.teamname
                ?.toLowerCase()
                ?.localeCompare(b?.teamname?.toLowerCase())
            ) ?? [],
    [businessTeams, formik.values.queueTeams]
  );
  /**
   * Change field with new value
   * @param name Name of field (if nested then last value is type)
   */
  const handleChangeForAutocomplete = (
    event: ChangeEvent<unknown>,
    newValue: any[]
  ) => {
    // keep autocomplete in sync
    setAutocomplete(newValue);
  };
  const setRefetchToggleForBusinessTeams = useSetRecoilState(
    refetchToggle(endpointEnum.GET_BUSINESS_TEAMS)
  );
  const [rowForEdit, handleRowForEdit] = useState({});
  const [teamEditModal, handleTeamEditModal] = useState(false);
  const handleEdit = (row) => {
    handleTeamEditModal(true);
    handleRowForEdit(row);
  };
  // notification using snackbar
  const { enqueueSnackbarWithStyles } = useSnackbarWithStyles();
  return (
    <>
      {/* alignItems: 'center', // set native height for button since flex sets height to be the same for all items */}
      <MaterialAutocomplete
        className={`flex-grow-1 ${classes.field}`}
        type="text"
        label="Teams"
        size="small"
        variant="outlined"
        options={teamsNotInQueue.current}
        value={autocomplete}
        multiple={true}
        onChange={handleChangeForAutocomplete}
        getOptionLabel={(option) => option.teamname ?? ''}
        isOptionEqualToValue={(option, value) =>
          option?.teamid === value?.teamid
        }
        disabled={businessTeams.state === 'loading'} // in progress or queue loading
      />
      <FieldArray
        name="queueTeams"
        render={(arrayHelper) => (
          <>
            <Button
              className={classes.field}
              variant="contained"
              color="primary"
              onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                autocomplete.forEach((item: IQueueTeamsContent) =>
                  arrayHelper.push(item)
                );
                setAutocomplete([]);
              }}
              disableElevation
            >
              Add
            </Button>
            <Table className={classes.table} aria-label="simple table">
              <TableHead>
                <TableRow>
                  <TableCell valign="baseline">Team</TableCell>
                  <TableCell valign="baseline">Count</TableCell>
                  <TableCell align="right" valign="baseline" width="100">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(businessTeams.state === 'loading' ||
                  !formik.values.queueTeams) && (
                  <TableRow>
                    <TableCell>
                      <DefaultActionSkeleton />
                    </TableCell>
                    <TableCell>
                      <DefaultActionSkeleton />
                    </TableCell>
                    <TableCell>
                      <DefaultActionSkeleton />
                    </TableCell>
                  </TableRow>
                )}
                {formik.values.queueTeams?.map(
                  (team: IQueueTeamsContent, index: number) => (
                    <TableRow key={team.teamid}>
                      <TableCell valign="baseline">
                        <Link onClick={() => handleEdit(team)} to="#">
                          {team.teamname}
                        </Link>
                      </TableCell>
                      <TableCell valign="baseline">
                        {team.memberCount}
                      </TableCell>
                      <TableCell align="right" valign="baseline" padding="none">
                        <IconButton
                          aria-label="delete"
                          onClick={(event) => {
                            arrayHelper.remove(index);
                          }}
                          className={classes.deleteIconButton}
                          size="large"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  )
                )}
              </TableBody>
            </Table>
          </>
        )}
      />
      <ViewQueueTeam
        show={!!teamEditModal}
        setRefetchToggleForBusinessTeams={setRefetchToggleForBusinessTeams}
        id={(rowForEdit as any)?.teamid ?? 0}
        onHide={() => {
          handleTeamEditModal(false);
          onTeamChange();
        }}
        enqueueSnackbarWithStyles={enqueueSnackbarWithStyles}
      />
    </>
  );
};
